import { useCallback, useEffect, useState } from "react";
import { Eye, EyeOff, Loader2, PenLine, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { absolute } from "../../lib/share";
import { EmptyRow, PanelTitle, StatCard, Table } from "../office/ui";
import { MaterialThumb } from "../office/marketing/MaterialGrid";
import { UploadBox } from "../office/marketing/UploadBox";
import { CaptionEditor } from "./CaptionEditor";

const TYPE_LABEL = { pdf: "PDF", video: "Video", image: "Imagen" };

export const MarketingAdminPanel = () => {
  const [items, setItems] = useState(null);
  const [stats, setStats] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const [m, s] = await Promise.all([api.get("/admin/marketing/materials"), api.get("/admin/marketing/stats")]);
      setItems(m.data);
      setStats(s.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const patch = async (m, body) => {
    try {
      await api.patch(`/admin/marketing/materials/${m.slug}`, body);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const remove = async (m) => {
    if (!window.confirm(`¿Eliminar "${m.title}"? Dejará de aparecer para la red.`)) return;
    await api.delete(`/admin/marketing/materials/${m.slug}`);
    load();
  };

  if (!items || !stats) return <Loader2 className="h-6 w-6 animate-spin" />;
  const admin = items.filter((m) => m.owner === "admin");
  const dists = items.filter((m) => m.owner !== "admin");
  const clicks = Object.fromEntries(stats.by_material.map((x) => [x.slug, x.clicks]));

  return (
    <div data-testid="marketing-admin">
      <PanelTitle title="Marketing de la red" text="Todo lo que subes aquí llega a la oficina virtual de todos los distribuidores para que lo re-publiquen en sus redes con su enlace personal. Edita los textos por red social y revisa qué material genera más clics." />
      <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard dark label="Clics en enlaces de la red" value={stats.total_clicks} testId="mk-clicks" />
        <StatCard label="Veces compartido" value={stats.total_shares} testId="mk-shares" />
        <StatCard label="Distribuidores con redes" value={stats.distributors_with_socials} testId="mk-socials" />
        <StatCard label="Materiales activos" value={admin.filter((m) => m.active).length} hint={`${dists.length} subidos por distribuidores`} testId="mk-materials" />
      </div>

      <h3 className="mt-8 font-display text-xl font-semibold">Subir material para toda la red</h3>
      <div className="mt-3"><UploadBox onUploaded={load} hint="PDF, MP4/MOV o JPG/PNG · hasta 250 MB · visible para todos los distribuidores" /></div>

      <h3 className="mt-8 font-display text-xl font-semibold">Materiales de la empresa</h3>
      <Table headers={["Material", "Tipo", "Clics", "Visible", "", ""]} testId="mk-table" minWidth="min-w-[800px]">
        {admin.map((m, i) => (
          <tr key={m.slug} className={`border-b border-ink/5 last:border-0 ${m.active ? "" : "opacity-50"}`} data-testid={`mk-row-${i}`}>
            <td className="px-4 py-3"><div className="flex items-center gap-3"><div className="h-12 w-20 shrink-0 overflow-hidden rounded-lg bg-ink"><MaterialThumb item={m} /></div><div><p className="font-semibold">{m.title}</p><a href={absolute(m.url)} target="_blank" rel="noreferrer" className="text-xs text-leaf-deep underline">ver archivo</a></div></div></td>
            <td className="px-4 py-3">{TYPE_LABEL[m.type]}</td>
            <td className="px-4 py-3 font-semibold">{clicks[m.slug] || 0}</td>
            <td className="px-4 py-3"><button type="button" onClick={() => patch(m, { active: !m.active })} className="inline-flex items-center gap-1 rounded-full border border-ink/15 px-3 py-1.5 text-xs font-semibold" data-testid={`mk-toggle-${i}`}>{m.active ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />} {m.active ? "Sí" : "No"}</button></td>
            <td className="px-4 py-3"><button type="button" onClick={() => setEditing(m)} className="inline-flex items-center gap-1 rounded-full bg-ink px-3 py-1.5 text-xs font-semibold text-white" data-testid={`mk-edit-${i}`}><PenLine className="h-3.5 w-3.5" /> Textos</button></td>
            <td className="px-4 py-3"><button type="button" onClick={() => remove(m)} className="rounded-full p-2 text-ink/50 hover:bg-coral/10 hover:text-coral" aria-label="Eliminar" data-testid={`mk-delete-${i}`}><Trash2 className="h-4 w-4" /></button></td>
          </tr>
        ))}
      </Table>

      <h3 className="mt-8 font-display text-xl font-semibold">Archivos subidos por distribuidores</h3>
      <Table headers={["Material", "Distribuidor", "Tipo", "Clics", ""]} testId="mk-dist-table" minWidth="min-w-[700px]">
        {!dists.length && <EmptyRow cols={5} text="Ningún distribuidor ha subido archivos todavía." testId="mk-dist-empty" />}
        {dists.map((m, i) => (
          <tr key={m.slug} className="border-b border-ink/5 last:border-0" data-testid={`mk-dist-row-${i}`}>
            <td className="px-4 py-3"><a href={absolute(m.url)} target="_blank" rel="noreferrer" className="font-semibold underline">{m.title}</a></td>
            <td className="px-4 py-3">{m.owner_name} <span className="font-mono text-xs text-ink/60">{m.owner}</span></td>
            <td className="px-4 py-3">{TYPE_LABEL[m.type]}</td>
            <td className="px-4 py-3">{clicks[m.slug] || 0}</td>
            <td className="px-4 py-3"><button type="button" onClick={() => remove(m)} className="rounded-full p-2 text-ink/50 hover:bg-coral/10 hover:text-coral" aria-label="Eliminar" data-testid={`mk-dist-delete-${i}`}><Trash2 className="h-4 w-4" /></button></td>
          </tr>
        ))}
      </Table>

      <h3 className="mt-8 font-display text-xl font-semibold">Distribuidores con más clics</h3>
      <Table headers={["Distribuidor", "Compartió", "Clics"]} testId="mk-top-table" minWidth="min-w-[500px]">
        {!stats.by_distributor.length && <EmptyRow cols={3} text="Aún no hay clics registrados." testId="mk-top-empty" />}
        {stats.by_distributor.map((x, i) => (
          <tr key={x.dist_id} className="border-b border-ink/5 last:border-0" data-testid={`mk-top-${i}`}>
            <td className="px-4 py-3"><p className="font-semibold">{x.name}</p><p className="font-mono text-xs text-ink/60">{x.dist_id}</p></td>
            <td className="px-4 py-3">{x.shares}</td>
            <td className="px-4 py-3 font-semibold">{x.clicks}</td>
          </tr>
        ))}
      </Table>
      {editing && <CaptionEditor material={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
};
